import { EyeNoneIcon, EyeOpenIcon } from '@radix-ui/react-icons';
import { Box, TextField } from '@radix-ui/themes';
import { FormHint } from 'components/form/hint';
import { FormInputWrapper } from 'components/form/input-wrapper';
import { FormLabel } from 'components/form/label';
import { EB } from 'components/layout/error-boundary';
import { FormContext } from 'contexts/form';
import { isValid } from 'date-fns';
import { InputErrorType, RegexFilter } from 'enums/form';
import { FormInputDef } from 'interfaces/form/input';
import {
  extendedErrorDetection,
  FormInputChangeDef,
} from 'interfaces/form/input-change';
import { FormInputWrapperDef } from 'interfaces/form/input-wrapper';
import { DATE_FORMAT } from 'lib/utilities/date';
import { StringUtil } from 'lib/utilities/string';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';

export const FormTextField = (
  props: FormInputDef &
    FormInputWrapperDef &
    FormInputChangeDef & {
      type?:
        | 'number'
        | 'search'
        | 'time'
        | 'text'
        | 'hidden'
        | 'date'
        | 'datetime-local'
        | 'email'
        | 'month'
        | 'password'
        | 'tel'
        | 'url'
        | 'week';

      defaultValue?: string;
      regex?: RegexFilter;
      slot?: ReactNode;
    }
) => {
  const { lastSubmitted, addError, removeError } = useContext(FormContext);

  const [touched, setTouched] = useState(!props.defaultTouched);

  const regex = useMemo(() => {
    switch (props.regex) {
      case RegexFilter.Alpha: {
        return /[^a-zA-Z]/g;
      }

      case RegexFilter.AlphaNumeric: {
        return /\W/g;
      }

      case RegexFilter.Whitespace: {
        return /\s/g;
      }

      default: {
        return undefined;
      }
    }
  }, [props.regex]);

  const [value, setValue] = useState<string>(props.defaultValue ?? '');

  const errorType = useMemo(() => {
    if (props.required && !value.trim()) {
      return InputErrorType.Required;
    }

    return extendedErrorDetection(value, props) ?? InputErrorType.None;
  }, [value]);

  useEffect(() => {
    if (!props.label) {
      return;
    }

    (errorType === InputErrorType.None ? removeError : addError)(props.label);
  }, [props.label, errorType]);

  const dateFormatHint = useMemo(() => {
    return props.onDateChange || props.onOptionalDateChange
      ? `e.g. ${StringUtil.printDate(new Date())}`
      : undefined;
  }, [props.onDateChange, props.onOptionalDateChange]);

  const [hidingPwd, setHidingPwd] = useState(true);

  const safeType =
    props.type === 'password' ? (hidingPwd ? 'password' : 'text') : props.type;

  return (
    <EB>
      <FormInputWrapper {...props}>
        <FormLabel {...props} />

        <TextField.Root
          placeholder={props.placeholder ?? `Type a value for ${props.label}`}
          type={safeType}
          value={value}
          onChange={(e) => {
            const v = regex
              ? e.target.value.replace(regex, '')
              : e.target.value;

            setValue(v);
            setTouched(true);

            if (props.onChange) {
              props.onChange(v.trim());
            }

            if (props.onOptionalChange) {
              props.onOptionalChange(v.trim() || undefined);
            }

            if (props.onNumericChange) {
              if (!v) {
                props.onNumericChange(0);
              } else {
                const maybeNum = StringUtil.tryParseNumber(v);
                if (maybeNum !== undefined) {
                  props.onNumericChange(maybeNum);
                }
              }
            }

            if (props.onOptionalNumericChange) {
              const maybeNum = StringUtil.tryParseNumber(v);
              props.onOptionalNumericChange(maybeNum);
            }

            if (props.onDateChange) {
              if (v.length !== DATE_FORMAT.length) {
                // do nothing
              } else {
                const date = new Date(v);
                if (isValid(date)) {
                  // get rid of Z since mongoose treats dates as local
                  props.onDateChange(date.toISOString().slice(0, -1), date);
                }
              }
            }

            if (props.onOptionalDateChange) {
              if (v.length !== DATE_FORMAT.length) {
                props.onOptionalDateChange(undefined, undefined);
              } else {
                const date = new Date(v);
                if (isValid(date)) {
                  // get rid of Z since mongoose treats dates as local
                  props.onOptionalDateChange(
                    date.toISOString().slice(0, -1),
                    date
                  );
                }
              }
            }

            if (props.onPasswordChange) {
              props.onPasswordChange(v.trim());
            }

            if (props.onOptionalPasswordChange) {
              props.onOptionalPasswordChange(v.trim() || undefined);
            }
          }}
        >
          {props.slot && <TextField.Slot>{props.slot}</TextField.Slot>}

          {props.type === 'password' && (
            <TextField.Slot side="right" className="clickable">
              {hidingPwd ? (
                <EyeOpenIcon onClick={() => setHidingPwd(false)} />
              ) : (
                <EyeNoneIcon onClick={() => setHidingPwd(true)} />
              )}
            </TextField.Slot>
          )}
        </TextField.Root>

        <Box title={dateFormatHint}>
          <FormHint
            hint={props.hint}
            error={errorType}
            touched={touched || !!lastSubmitted}
          />
        </Box>
      </FormInputWrapper>
    </EB>
  );
};
